import type { LivePriceDto } from "@intelligems/ig-types/dtos/live-prices";
import { useGetBillingSubscriptionAccess } from "@src/api/billing-modules/subscription-access";
import { useGetModularLivePrices } from "@src/api/intelligems/get-live-prices";
import { moduleKeyForLivePrice } from "@src/components/BillingSettings2026/workflow";

export interface LiveModuleTrial {
  moduleId: string;
  stripeProductId: string;
  stripePriceId: string;
  startDate: number;
  endDate: number;
  endBehavior: "cancels" | "converts" | "converts_cancellable";
}

// Keyed by product id, not stripePriceId — a trial's price can be an off-catalog promo absent from livePrices, but its product id always resolves to the canonical module key via moduleKeyForLivePrice.
const buildModuleIdByProductId = (
  prices: readonly LivePriceDto[]
): Map<string, string> =>
  new Map(
    prices.map((price) => [price.product.id, moduleKeyForLivePrice(price)])
  );

export const useModuleTrials = (): LiveModuleTrial[] => {
  const subscriptionAccess = useGetBillingSubscriptionAccess();
  const livePrices = useGetModularLivePrices();

  const moduleIdByProductId = buildModuleIdByProductId(
    livePrices.data?.prices ?? []
  );
  const nowMs = Date.now();

  const trials: LiveModuleTrial[] = [];
  for (const trial of subscriptionAccess.data?.moduleTrials ?? []) {
    const moduleId = moduleIdByProductId.get(trial.stripeProductId);
    if (!moduleId || trial.endDate * 1_000 <= nowMs) {
      continue;
    }
    trials.push({
      moduleId,
      stripeProductId: trial.stripeProductId,
      stripePriceId: trial.stripePriceId,
      startDate: trial.startDate,
      endDate: trial.endDate,
      // Absent on rollout skew; the schema mandates treating that as "converts".
      endBehavior: trial.endBehavior ?? "converts",
    });
  }

  return trials;
};
